import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { loadArticles, deleteArticle } from "../../../api/articles";
import { ROUTES } from "../../../config/routes";
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { formatDate } from '../../../utils/articleUtils';
import DataTable from 'react-data-table-component';
import './AllArticlesPage.css';
import ExcelJS from 'exceljs';

const AllArticlesPage = () => {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  
  const history = useHistory();

  useEffect(() => {
    fetchArticles();
  }, []);

  const fetchArticles = async () => {
    try {
      const articlesData = await loadArticles(false);
      setArticles(articlesData);
    } catch (error) {
      console.error('Error fetching articles: ', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (id) => {
    history.push(`${ROUTES.CREATE_ARTICLE.path}?aid=${id}`);
  };

  const handleDelete = async (articleId) => {
    try {
      setLoading(true);
      const result = await deleteArticle(articleId);
      if (result.success) {
        setArticles(articles.filter(article => article.id !== articleId));
        toast.success('Article supprimé avec succès.');
      } else {
        toast.error("Echec de suppression d'article. Essayer plus tard.");
      }
    } catch (error) {
      toast.error("Echec de suppression d'article.");
      console.error('Error deleting article: ', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateArticle = () => {
    history.push(ROUTES.CREATE_ARTICLE.path);
    window.scrollTo(0, 0);
  };

  const handleBack = () => {
    history.push(ROUTES.DASHBOARD.path); 
    window.scrollTo(0, 0);
  };
  
  const handleExportExcel = () => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Articles");
    
    // Add headers
    worksheet.columns = [
      { header: 'Title', key: 'title', width: 30 },
      { header: 'Category', key: 'category', width: 20 },
      { header: 'Date', key: 'date', width: 15 },
      { header: 'Description', key: 'description', width: 50 }
    ];
    
    // Add rows
    articles.forEach(article => {
      worksheet.addRow({
        title: article.title,
        category: article.category,
        date: formatDate(article.date),
        description: article.description
      });
    });
    
    // Generate file name with current date
    const fileName = `articles_export_${new Date().toISOString().split('T')[0]}.xlsx`;
    
    // Export to file
    workbook.xlsx.writeBuffer().then(buffer => {
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Export Excel réussi');
    });
  };

  const columns = [
    {
      name: '#',
      selector: (row, index) => index + 1,
      sortable: true,
      width: '40px', // Reduced width for compactness
    },
    {
      name: 'Image',
      selector: row => row.image,
      cell: row => row.image ? <img src={row.image} alt={row.title} className="article-image" style={{ width: "100px", height: "auto" }} /> : 'N/A',
    },
    {
      name: 'Title',
      selector: row => row.title,
      sortable: true,
    },
    {
      name: 'Category',
      selector: row => row.category,
      sortable: true,
    },
    {
      name: 'Date',
      selector: row => row.date,
      sortable: true,
      cell: row => formatDate(row.date),
    },
    {
      name: 'Description',
      selector: row => row.description,
      cell: row => <textarea rows="5" value={row.description} readOnly className="form-control-plaintext" />,
    },
    {
      name: 'Actions',
      cell: row => (
        <div className="submit-btn action-buttons" style={{ marginBottom: "1rem", minWidth: "150px" }}>
          <button className="btn btn-primary btn-action" onClick={() => handleEdit(row.id)} title="Editer">
            <i className="bi bi-pencil"></i>
          </button>
          <button className="btn btn-danger btn-action" onClick={() => handleDelete(row.id)} title="Supprimer">
            <i className="bi bi-trash"></i>
          </button>
        </div>
      ),
    },
  ];

  const customStyles = {
    table: {
      style: {
        // Extend table width by adding extra space on the right
        width: 'calc(100% + 50px)',
      },
    },
    header: {
      style: {
        minHeight: '56px',
        backgroundColor: '#e3f2fd',
      },
    },
    headRow: {
      style: {
        backgroundColor: '#e3f2fd',
      },
    },
    headCells: {
      style: {
        paddingLeft: '8px',
        paddingRight: '8px',
      },
    },
    cells: {
      style: {
        paddingLeft: '8px',
        paddingRight: '8px',
      },
    },
    rows: {
      style: {
        minHeight: '50px', // Reduced row height for compact table
        '&:nth-of-type(odd)': {
          backgroundColor: '#f1f8ff',
        },
      },
    },
  };

  return (
    <div className="contact-wrapper overflow-hidden">
      <ToastContainer />
      <div className="container pt-120 position-relative">
        <div className="row">
          <div className="col-lg-8"></div>
          <div className="articles-container">
            <h1>Articles</h1>
            <div className="d-flex mb-4 mt-4">
              <button className="btn btn-primary me-2" onClick={handleCreateArticle}>
                <i className="bi bi-plus" title="Créer un article"></i>
              </button>
              <button className="btn btn-success" onClick={handleExportExcel}>
                <i className="bi bi-file-excel me-1"></i>
                Exporter Excel
              </button>
            </div>
            {loading ? (
              <div>Loading...</div>
            ) : (
              <DataTable
                columns={columns}
                data={articles}
                pagination
                highlightOnHover
                responsive
                striped
                dense
                customStyles={customStyles}
              />
            )}
            <div className="submit-btn">
              <button type="button" onClick={handleBack} className="btn btn-secondary">
                <i className="bi bi-arrow-left" title="Retour au dashboard"></i>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AllArticlesPage;